import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

// Silence non-essential console output in every environment where debug mode is
// off (see environment.debugMode — currently false for local, dev, staging and
// prod). This neutralises noisy tracing from our own code AND from third-party
// libraries (e.g. CKEditor's upload adapter) that call console.* directly.
// warn/error are deliberately left intact: they are load-bearing for error
// reporting / Sentry. Flip environment.debugMode to true to restore logging.
if (!environment.debugMode) {
  const noop = (): void => {};
  const silenced = [
    'log', 'debug', 'info', 'trace', 'dir', 'dirxml', 'table',
    'group', 'groupCollapsed', 'groupEnd', 'count', 'countReset',
    'time', 'timeEnd', 'timeLog',
  ] as const;
  for (const method of silenced) {
    (console as unknown as Record<string, unknown>)[method] = noop;
  }
}

// Zone-based change detection, explicitly.
//
// Up to Angular 18, bootstrapModule() always created a real NgZone, so every async
// callback — an HTTP response, a timer, a socket event — re-entered the Angular zone
// and Angular repainted on its own. Angular 19+ flipped the default:
// PlatformRef.bootstrapModuleFactory() now prepends provideZonelessChangeDetectionInternal(),
// so NgZone is a NoopNgZone and nothing except Angular's own DOM listeners, markForCheck()
// and signals schedules a render. This app is written against the old contract (components
// assign to a field inside subscribe() and expect the view to follow), so after the 18→22
// upgrade those views only repainted when an unrelated click happened to trigger a tick —
// the mailbox, for instance, sat on its skeleton rows until the user clicked the page.
//
// It has to be passed here rather than in AppModule.providers: for the NgModule bootstrap
// path Angular only reads it from `applicationProviders` (see the note on
// provideZoneChangeDetection, and PROVIDED_NG_ZONE, in @angular/core).
// zone.js is still loaded from polyfills.ts, so opting back in is all that is needed.
platformBrowserDynamic()
  .bootstrapModule(AppModule, {
    applicationProviders: [provideZoneChangeDetection({ eventCoalescing: true })],
  })
  .catch(err => console.error(err));
